import { origin } from '../components/BackBar';
import { Signal } from '@verlo/shared/components/ui/signal';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { cashApi, ordersApi } from '@verlo/shared/services/api';
import { Card, CardContent, CardHeader, CardTitle } from '@verlo/shared/components/ui/card';
import { Button } from '@verlo/shared/components/ui/button';
import { Skeleton } from '@verlo/shared/components/ui/skeleton';
import { Segmented } from '@verlo/shared/components/ui/segmented';
import AttentionBoard from '../components/orders/AttentionBoard';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@verlo/shared/components/ui/table';
import { toast } from 'sonner';
import { RefreshCw, Eye, Clock, CheckCircle, XCircle, ChefHat, Utensils, CreditCard } from 'lucide-react';
import { orderRef } from '@verlo/shared/lib/orderRef';

// Dishes and drinks, not lines: two lagers on one line are two. A voided line counts nothing.
const itemCount = (order) => (order.items || []).reduce((n, l) => n + (l.voided ? 0 : (Number(l.quantity) || 1)), 0);

const statusConfig = {
  pending: { label: 'Pending', tone: 'warn', icon: Clock },
  in_progress: { label: 'Cooking', tone: 'info', icon: ChefHat },
  ready: { label: 'Ready', tone: 'ok', icon: CheckCircle },
  delivered: { label: 'Delivered', tone: 'info', icon: Utensils },
  paid: { label: 'Paid', tone: null, icon: CreditCard },
  cancelled: { label: 'Cancelled', tone: 'stop', icon: XCircle },
};

const OrdersPage = () => {
  const navigate = useNavigate();
  const [allOrders, setAllOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('today');
  // The venue's trading day, from the drawer, so a venue still serving at 00:10 reads as
  // today and not as "0 active today · 17 still open from earlier days" (Chris's review H5).
  const [tradingDate, setTradingDate] = useState(null);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      // FIND-1: request the 7-day window the tabs actually render (Today/Yesterday/Week),
      // rather than the top 200 by recency — a busy site trades >100 orders in under a day,
      // so recency-only truncation made Yesterday/Week incomplete and their totals wrong.
      // `since` bounds the result by time; the server still caps the count as a safety net.
      const since = new Date();
      since.setHours(0, 0, 0, 0);
      since.setDate(since.getDate() - 7);
      const res = await ordersApi.getAll(null, 2000, since.toISOString());
      setAllOrders(res.data);
    } catch (err) {
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
    cashApi.drawer().then((res) => setTradingDate(res.data?.trading_date || null)).catch(() => {});
  }, []);

  // Filter orders by date
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  
  const sevenDaysAgo = new Date(today);
  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

  // A day is the venue's trading day where both sides know it: the order carries the one
  // it was numbered on (`order_numbers.stamp`) and the drawer says which one we are in.
  // Orders taken before numbering began have none, so those fall back to the calendar date.
  const dayBefore = (iso) => {
    const d = new Date(`${iso}T12:00:00Z`);
    d.setUTCDate(d.getUTCDate() - 1);
    return d.toISOString().slice(0, 10);
  };

  const filterOrders = (filter) => {
    return allOrders.filter((order) => {
      const orderDate = new Date(order.created_at);
      orderDate.setHours(0, 0, 0, 0);
      const trading = tradingDate && order.trading_date ? order.trading_date : null;

      switch (filter) {
        case 'today':
          return trading ? trading === tradingDate : orderDate.getTime() === today.getTime();
        case 'yesterday':
          return trading ? trading === dayBefore(tradingDate) : orderDate.getTime() === yesterday.getTime();
        case 'week':
          return orderDate >= sevenDaysAgo;
        case 'active':
          // `kitchen_status`, not `status` — a bar order is often paid before the kitchen
          // has made it, which sets `status` to 'paid' and would otherwise drop it from
          // "active" the instant it's paid (#37, same cause as the kitchen display).
          return ['pending', 'in_progress', 'ready'].includes(order.kitchen_status || order.status);
        default:
          return true;
      }
    });
  };

  const todayOrders = filterOrders('today');
  const yesterdayOrders = filterOrders('yesterday');
  const weekOrders = filterOrders('week');
  const activeOrders = filterOrders('active');
  // Open orders from before today: "17 active" sat over an empty Today tab (Chris's review H4).
  const todayIds = new Set(todayOrders.map((o) => o.id));
  const olderOpen = activeOrders.filter((o) => !todayIds.has(o.id));

  const handleStatusChange = async (orderId, newStatus) => {
    try {
      await ordersApi.updateStatus(orderId, newStatus);
      toast.success('Status updated');
      fetchOrders();
    } catch (err) {
      toast.error('Failed to update');
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(value);
  };

  const formatTime = (dateStr) => {
    return new Date(dateStr).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
  };

  // Calculate totals
  const calcTotal = (orders) => orders.reduce((sum, o) => sum + (o.status === 'paid' ? o.total : 0), 0);

  const OrdersTable = ({ orders, showDate = false, empty = 'No orders found' }) => (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Order</TableHead>
          <TableHead>Table/Type</TableHead>
          <TableHead>Items</TableHead>
          <TableHead>Total</TableHead>
          <TableHead>Status</TableHead>
          <TableHead>{showDate ? 'Date' : 'Time'}</TableHead>
          <TableHead className="text-right">Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {orders.length > 0 ? (
          orders.map((order) => {
            const config = statusConfig[order.status] || statusConfig.pending;
            return (
              <TableRow key={order.id} className="cursor-pointer hover:bg-surface-2" onClick={() => navigate(`/orders/${order.id}`, { state: origin('/orders', 'Orders') })}>
                <TableCell className="font-mono text-xs text-ink-soft">{orderRef(order)}</TableCell>
                <TableCell className="font-medium">{order.table_name}</TableCell>
                <TableCell className="text-ink-soft">
                  {itemCount(order)} {itemCount(order) === 1 ? 'item' : 'items'}
                </TableCell>
                <TableCell className="font-semibold">{formatCurrency(order.total)}</TableCell>
                <TableCell>
                  <Signal tone={config.tone || 'info'}>{config.label}</Signal>
                </TableCell>
                <TableCell className="text-ink-soft">{showDate ? formatDate(order.created_at) : formatTime(order.created_at)}</TableCell>
                <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                  <Button
                    variant="ghost"
                    size="sm"
                    aria-label={`View order for ${order.table_name}`}
                    onClick={() => navigate(`/orders/${order.id}`, { state: origin('/orders', 'Orders') })}
                  >
                    <Eye size={16} />
                  </Button>
                </TableCell>
              </TableRow>
            );
          })
        ) : (
          <TableRow>
            <TableCell colSpan={7} className="text-center py-8 text-ink-soft" data-testid="orders-empty">
              {empty}
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  );

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-32" />
        <div className="grid grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-20 rounded-lg" />)}
        </div>
        <Skeleton className="h-96 rounded-lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="orders-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold font-heading text-ink">Orders</h1>
          <p className="text-ink-soft mt-1">
            {activeOrders.length - olderOpen.length} active today
            {olderOpen.length > 0 && <> · {olderOpen.length} still open from earlier days</>}
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchOrders}>
          <RefreshCw size={14} className="mr-2" /> Refresh
        </Button>
      </div>

      {/* What needs a manager right now — the board (backend/attention.py). */}
      <AttentionBoard onChanged={fetchOrders} />

      {/* The ledger */}
      <Card className="bg-white border-0 shadow-sm">
        <CardHeader className="pb-3 flex flex-row items-center justify-between space-y-0">
          <Segmented
            value={activeTab}
            onChange={setActiveTab}
            options={[
              { value: 'today', label: `Today · ${todayOrders.length}` },
              { value: 'yesterday', label: `Yesterday · ${yesterdayOrders.length}` },
              { value: 'week', label: `7 days · ${weekOrders.length}` },
            ]}
          />
          <span className="text-xs text-ink-soft">
            {activeOrders.length - olderOpen.length} active · {formatCurrency(calcTotal(todayOrders))} taken today
          </span>
        </CardHeader>
        <CardContent className="p-0">
          {activeTab === 'today' && (
            <OrdersTable orders={todayOrders} empty={olderOpen.length ? (
              <>
                No orders today. {olderOpen.length} still open from earlier days.{' '}
                <button type="button" className="font-semibold text-verlo hover:underline" onClick={() => setActiveTab('week')} data-testid="orders-show-older">Show the last 7 days</button>
              </>
            ) : 'No orders today'} />
          )}
          {activeTab === 'yesterday' && <OrdersTable orders={yesterdayOrders} />}
          {activeTab === 'week' && <OrdersTable orders={weekOrders} showDate />}
        </CardContent>
      </Card>
    </div>
  );
};

export default OrdersPage;
